import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, HelpCircle, BookOpen, MessageCircle, Phone, Mail, ChevronDown, ChevronRight } from 'lucide-react';

function HelpPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  const helpCategories = [
    {
      title: 'Getting Started',
      icon: BookOpen,
      articles: [
        'How to create an account',
        'Setting up your profile',
        'Navigating the platform',
        'Understanding course structure'
      ]
    },
    {
      title: 'Course Management',
      icon: BookOpen,
      articles: [
        'Enrolling in courses',
        'Accessing course materials',
        'Tracking your progress',
        'Downloading certificates'
      ]
    },
    {
      title: 'Technical Support',
      icon: HelpCircle,
      articles: [
        'Browser compatibility issues',
        'Video playback problems',
        'Mobile app troubleshooting',
        'Account recovery'
      ]
    },
    {
      title: 'Billing & Payments',
      icon: MessageCircle,
      articles: [
        'Payment methods accepted',
        'Refund policy',
        'Subscription management',
        'Invoice and receipts'
      ]
    }
  ];

  const faqs = [
    {
      question: 'How do I enroll in a course?',
      answer: 'To enroll in a course, simply browse our course catalog, select the course you\'re interested in, and click the "Enroll Now" button. You\'ll be redirected to our secure payment page where you can complete your purchase.'
    },
    {
      question: 'Can I access courses on mobile devices?',
      answer: 'Yes! Our platform is fully responsive and works on all devices including smartphones and tablets. We also have dedicated mobile apps available for iOS and Android.'
    },
    {
      question: 'What if I\'m not satisfied with a course?',
      answer: 'We offer a 30-day money-back guarantee for all courses. If you\'re not satisfied with your purchase, you can request a full refund within 30 days of enrollment.'
    },
    {
      question: 'How long do I have access to course materials?',
      answer: 'Once enrolled, you have lifetime access to all course materials, including videos, resources, and updates. You can learn at your own pace without any time restrictions.'
    },
    {
      question: 'Do you offer certificates upon completion?',
      answer: 'Yes! Upon successful completion of a course, you\'ll receive a certificate of completion that you can download and share on your professional profiles.'
    },
    {
      question: 'Can I get help if I\'m stuck on a course?',
      answer: 'Absolutely! We have a dedicated support team and active community forums where you can ask questions and get help from instructors and fellow students.'
    }
  ];

  const contactMethods = [
    {
      icon: MessageCircle,
      title: 'Live Chat',
      description: 'Get instant help from our support team',
      action: 'Start Chat',
      available: 'Available 24/7'
    },
    {
      icon: Mail,
      title: 'Email Support',
      description: 'Send us a detailed message',
      action: 'Send Email',
      available: 'Response within 24 hours'
    },
    {
      icon: Phone,
      title: 'Phone Support',
      description: 'Speak directly with our team',
      action: 'Call Now',
      available: 'Mon-Fri, 9AM-6PM EST'
    }
  ];

  const filteredFAQs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-50 to-blue-50 py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl font-bold text-slate-900 mb-6">
              Help Center
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed mb-8">
              Find answers to your questions and get the support you need to succeed in your learning journey.
            </p>
            
            <div className="relative max-w-2xl mx-auto">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-6 h-6" />
              <input
                type="text"
                placeholder="Search for help articles, FAQs, or topics..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 text-lg border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-lg"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Help Categories */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Browse Help Topics</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {helpCategories.map((category, index) => (
                <motion.div
                  key={category.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-100"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                    <category.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  
                  <h3 className="text-lg font-bold text-slate-900 mb-4">{category.title}</h3>
                  
                  <ul className="space-y-2">
                    {category.articles.map((article, idx) => (
                      <li key={idx} className="text-sm text-slate-600 hover:text-blue-600 cursor-pointer transition-colors duration-200">
                        {article}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Frequently Asked Questions</h2>
            
            <div className="space-y-4">
              {filteredFAQs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl shadow-lg overflow-hidden"
                >
                  <button
                    onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-slate-50 transition-colors duration-200"
                  >
                    <h3 className="text-lg font-semibold text-slate-900">{faq.question}</h3>
                    {expandedFAQ === index ? (
                      <ChevronDown className="w-5 h-5 text-slate-500" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-slate-500" />
                    )}
                  </button>
                  
                  {expandedFAQ === index && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="px-6 pb-4"
                    >
                      <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
            
            {filteredFAQs.length === 0 && searchTerm && (
              <div className="text-center py-12">
                <HelpCircle className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2">No results found</h3>
                <p className="text-slate-600">Try searching with different keywords or browse our help categories above.</p>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Contact Support */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Still Need Help?</h2>
            <p className="text-lg text-slate-600 text-center mb-12">
              Our support team is here to help you succeed. Choose the method that works best for you.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {contactMethods.map((method, index) => (
                <motion.div
                  key={method.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 text-center border border-blue-100"
                >
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <method.icon className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{method.title}</h3>
                  <p className="text-slate-600 mb-4">{method.description}</p>
                  <p className="text-sm text-blue-600 font-semibold mb-6">{method.available}</p>
                  
                  <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold transition-colors duration-300">
                    {method.action}
                  </button>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Quick Links</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Popular Resources</h3>
                  <ul className="space-y-2">
                    <li><button className="text-blue-600 hover:text-blue-700 text-left">Getting Started Guide</button></li>
                    <li><button className="text-blue-600 hover:text-blue-700 text-left">Course Completion Tips</button></li>
                    <li><button className="text-blue-600 hover:text-blue-700 text-left">Mobile App Download</button></li>
                    <li><button className="text-blue-600 hover:text-blue-700 text-left">System Requirements</button></li>
                  </ul>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Community</h3>
                <ul className="space-y-2">
                  <li><button className="text-blue-600 hover:text-blue-700 text-left">Student Forums</button></li>
                  <li><button className="text-blue-600 hover:text-blue-700 text-left">Study Groups</button></li>
                  <li><button className="text-blue-600 hover:text-blue-700 text-left">Success Stories</button></li>
                  <li><button className="text-blue-600 hover:text-blue-700 text-left">Instructor Office Hours</button></li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default HelpPage;
